import { useQuery } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase.js';
import type { ClinicByokRow, ClinicModuleRow, ClinicProviderOption } from './types.js';

export const AI_CLINIC_QUERY_KEYS = {
  modules: (clinicId: string) => ['ai-clinic', 'modules', clinicId] as const,
  byok: (clinicId: string) => ['ai-clinic', 'byok', clinicId] as const,
  providers: ['ai-clinic', 'providers'] as const,
};

interface RawModuleConfig {
  id: string;
  module_key: string;
  name_pt_br: string;
  description: string | null;
  is_active: boolean;
}

interface RawModuleSetting {
  module_key: string;
  enabled: boolean;
  disabled_reason: string | null;
}

/**
 * Lista os módulos de IA do catálogo (apenas `is_active = true`) + merge
 * com os toggles já cadastrados pela clínica. Módulos sem linha em
 * `ai_clinic_module_settings` são tratados como `enabled = true`
 * (fail-open, espelhando o `clinic_ai_module_is_enabled` do banco).
 */
export function useClinicModules(clinicId: string | null) {
  return useQuery({
    enabled: !!clinicId,
    queryKey: clinicId ? AI_CLINIC_QUERY_KEYS.modules(clinicId) : ['ai-clinic', 'modules', 'none'],
    queryFn: async (): Promise<ClinicModuleRow[]> => {
      const [{ data: catalog, error: catalogError }, { data: settings, error: settingsError }] =
        await Promise.all([
          supabase
            .from('ai_module_configs')
            .select('id, module_key, name_pt_br, description, is_active')
            .eq('is_active', true)
            .order('module_key', { ascending: true }),
          // `ai_clinic_module_settings` foi criada na migration 0032 e ainda
          // não está em @kmgestao/db-types (regenerar via `pnpm db:gen-types`
          // após aplicar a migration). Até lá, cast via `as never` p/ fugir
          // do narrowing estrito do PostgrestClient.
          supabase
            .from('ai_clinic_module_settings' as never)
            .select('module_key, enabled, disabled_reason')
            .eq('clinic_id' as never, clinicId as never),
        ]);

      if (catalogError) throw catalogError;
      if (settingsError) throw settingsError;

      const settingsByKey = new Map<string, RawModuleSetting>();
      for (const s of (settings ?? []) as unknown as RawModuleSetting[]) {
        settingsByKey.set(s.module_key, s);
      }

      return ((catalog ?? []) as unknown as RawModuleConfig[]).map((c) => {
        const setting = settingsByKey.get(c.module_key);
        return {
          id: c.id,
          moduleKey: c.module_key as ClinicModuleRow['moduleKey'],
          namePtBr: c.name_pt_br,
          description: c.description,
          enabled: setting ? setting.enabled : true,
          disabledReason: setting?.disabled_reason ?? null,
        };
      });
    },
  });
}

interface RawByokRow {
  id: string;
  provider_id: string;
  model_id: string | null;
  nickname: string | null;
  api_key_last4: string;
  updated_at: string;
  verification_status: ClinicByokRow['verificationStatus'];
  ai_providers: { display_name: string } | null;
}

/**
 * Lê a chave BYOK ativa da clínica (se existir). Retorna `null` quando
 * não há chave (clínica usa a chave da plataforma).
 */
export function useClinicByok(clinicId: string | null) {
  return useQuery({
    enabled: !!clinicId,
    queryKey: clinicId ? AI_CLINIC_QUERY_KEYS.byok(clinicId) : ['ai-clinic', 'byok', 'none'],
    queryFn: async (): Promise<ClinicByokRow | null> => {
      const { data, error } = await supabase
        .from('ai_clinic_keys')
        .select(
          `
          id, provider_id, model_id, nickname, api_key_last4, updated_at, verification_status,
          ai_providers ( display_name )
          `,
        )
        .eq('clinic_id', clinicId as string)
        .eq('enabled', true)
        .is('deleted_at', null)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const row = data as unknown as RawByokRow;
      return {
        id: row.id,
        providerId: row.provider_id,
        providerName: row.ai_providers?.display_name ?? '—',
        modelId: row.model_id,
        nickname: row.nickname,
        apiKeyLast4: row.api_key_last4,
        updatedAt: row.updated_at,
        verificationStatus: row.verification_status,
      };
    },
  });
}

interface RawProviderOption {
  id: string;
  key: string;
  display_name: string;
  enabled: boolean;
}

/**
 * Providers habilitados no catálogo — opções disponíveis para a clínica
 * escolher ao cadastrar BYOK.
 */
export function useClinicAvailableProviders() {
  return useQuery({
    queryKey: AI_CLINIC_QUERY_KEYS.providers,
    queryFn: async (): Promise<ClinicProviderOption[]> => {
      const { data, error } = await supabase
        .from('ai_providers')
        .select('id, key, display_name, enabled')
        .eq('enabled', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      const rows = (data ?? []) as unknown as RawProviderOption[];
      return rows.map((r) => ({ id: r.id, key: r.key, displayName: r.display_name }));
    },
  });
}
